import { Link } from "react-router-dom";
import FaqAccordion from "../../components/shared/FaqAccordion.jsx";

const bodyStyle = { fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.85)", margin: 0 };
const sectionTitleStyle = {
  fontFamily: "var(--font-serif)",
  fontSize: "22px",
  color: "var(--color-ink)",
  margin: 0,
};
function Section({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <p style={sectionTitleStyle}>{title}</p>
      {children}
    </div>
  );
}

function Fund({ title, body, to, linkLabel }) {
  return (
    <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "24px 26px" }}>
      <p style={{ fontFamily: "var(--font-serif)", fontSize: "18px", color: "var(--color-ink)", margin: 0 }}>{title}</p>
      <p style={bodyStyle}>{body}</p>
      <Link to={to} style={{ fontSize: "14px" }}>
        {linkLabel}
      </Link>
    </div>
  );
}

/** Shared with SupportSubpage.jsx for the FAQPage JSON-LD — keep in sync
 * with the rendered FaqAccordion below. */
export const MEMBERSHIP_FAQ = {
  de: [
    {
      question: "Ist das eine Mitgliedschaft?",
      answer: "Nein — wir sind eine gUG, keine Mitgliedschaft im Vereinssinn. Du wirst Förderer, nicht Mitglied, und hast keine Pflichten.",
    },
    { question: "Kann ich kündigen?", answer: "Jederzeit — eine Mail reicht." },
  ],
  en: [
    {
      question: "Is this a membership?",
      answer: "No — we're a gUG, not an association with formal membership. You become a supporter, not a member, and have no obligations.",
    },
    { question: "Can I cancel?", answer: "Any time — an email is enough." },
  ],
};

/**
 * "Förderer" copy, as provided directly by the association (replaces the
 * earlier generic "Fördermitgliedschaft" placeholder wording). No numeric
 * membership fee is stated here — that stays a placeholder decision for
 * the association, same treatment as elsewhere on the site.
 * German is the primary/authoritative wording; English is a courtesy
 * translation.
 */
export function MembershipDe() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <p style={bodyStyle}>
          Große Abenteuer brauchen treue Gefährten – und genau das bist du als Förderer von Tales&amp;Legends.
        </p>
        <p style={bodyStyle}>
          Unsere Events entstehen mit viel Herzblut, ehrenamtlichem Engagement und einem kleinen, aber leidenschaftlichen
          Team. Als gemeinnützige Organisation reinvestieren wir alles, was wir einnehmen, direkt in die Qualität
          unserer Veranstaltungen: in Kulissen, Kostüme, Requisiten, Musik und die unzähligen kleinen Details, die aus
          einem Wochenende ein unvergessliches Erlebnis machen.
        </p>
        <p style={bodyStyle}>
          Du musst nicht auf einem Event dabei sein, um Teil von Tales&amp;Legends zu sein. Manchmal reicht es, im
          Hintergrund die Fackel zu halten – damit die Geschichte weitergeht.
        </p>
      </div>

      <Section title="Warum ein monatlicher Beitrag mehr wert ist als eine einmalige Spende">
        <p style={bodyStyle}>
          Lagermiete und Versicherung laufen jeden Monat, unabhängig davon, ob gerade ein Event stattfindet. Eine
          einmalige Spende hilft im Moment — ein monatlicher Beitrag gibt uns Planungssicherheit über das ganze Jahr
          und lässt uns Entscheidungen treffen, die länger als ein Quartal reichen.
        </p>
      </Section>

      <Section title="Wofür wir das immer brauchen">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: "16px" }}>
          <Fund
            title="Betrieb-Fonds"
            body="Lagermiete, Versicherung, Buchhaltung und IT laufen jeden Monat — unabhängig davon, ob gerade ein Event stattfindet. Dein Beitrag hält den laufenden Betrieb stabil."
            to="/de/spenden/betrieb-2026"
            linkLabel="Zum Betrieb-Fonds →"
          />
          <Fund
            title="Freiplatz-Fonds"
            body="Damit niemand nur wegen des Ticketpreises fernbleibt, brauchen wir laufend Geld für Frei- und Solidartickets — gerade dafür eignet sich ein monatlicher Beitrag am besten."
            to="/de/spenden/freiplatz-fonds-2026"
            linkLabel="Zum Freiplatz-Fonds →"
          />
        </div>
      </Section>

      <Section title="So funktioniert es">
        <p style={bodyStyle}>
          Wähle auf unserer <Link to="/de/spenden">Spendenseite</Link> den Bereich aus, den du unterstützen möchtest,
          und wähle im Spendenformular „Monatlich" statt „Einmalig". Bezahlt wird per Dauerauftrag oder
          PayPal-Abo, jederzeit mit einer Mail kündbar. Die Spendenquittungen werden durch unser System automatisch
          ausgestellt.
        </p>
      </Section>

      <Section title="Häufige Fragen">
        <FaqAccordion items={MEMBERSHIP_FAQ.de} />
      </Section>
    </div>
  );
}

/** Courtesy translation — German is the primary wording (see MembershipDe). */
export function MembershipEn() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <p style={bodyStyle}>
          Great adventures need loyal companions – and that's exactly what you are as a supporter of Tales&amp;Legends.
        </p>
        <p style={bodyStyle}>
          Our events come to life through heart, volunteer commitment, and a small but passionate team. As a non-profit
          organization, we reinvest everything we earn directly into the quality of our events: sets, costumes, props,
          music, and the countless small details that turn a weekend into an unforgettable experience.
        </p>
        <p style={bodyStyle}>
          You don't have to attend an event to be part of Tales&amp;Legends. Sometimes it's enough to hold the torch in
          the background – so the story can go on.
        </p>
      </div>

      <Section title="Why a monthly contribution is worth more than a one-off donation">
        <p style={bodyStyle}>
          Storage rent and insurance run every month, whether or not an event is happening right now. A one-off
          donation helps in the moment — a monthly contribution gives us planning certainty across the whole year
          and lets us make decisions that reach further than a single quarter.
        </p>
      </Section>

      <Section title="What We Always Need">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: "16px" }}>
          <Fund
            title="Operations Fund"
            body="Storage rent, insurance, accounting, and IT run every month — whether or not an event is happening right now. Your contribution keeps day-to-day operations stable."
            to="/en/donate/operations-2026"
            linkLabel="To the Operations Fund →"
          />
          <Fund
            title="Free Ticket Fund"
            body="So nobody stays away just because of the ticket price, we need ongoing money for free and subsidised tickets — exactly the kind of thing a monthly contribution suits best."
            to="/en/donate/free-ticket-fund-2026"
            linkLabel="To the Free Ticket Fund →"
          />
        </div>
      </Section>

      <Section title="How It Works">
        <p style={bodyStyle}>
          Choose the project you'd like to support on our <Link to="/en/donate">donations page</Link>, and select
          "Monthly" instead of "One-time" in the donation form. Payment runs by standing order or PayPal
          subscription, cancellable any time with an email. Donation receipts are issued automatically by our
          system.
        </p>
      </Section>

      <Section title="Frequently Asked Questions">
        <FaqAccordion items={MEMBERSHIP_FAQ.en} />
      </Section>
    </div>
  );
}
